import classNames from 'classnames';
import { InputHTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Input from './Input';
import { DateRange, DayPicker, getDefaultClassNames } from 'react-day-picker';

import './DatePicker.css';

import { format } from 'date-fns';
import { CalendarDaysIcon } from 'lucide-react';

interface DatePickerRangeProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  className?: string;
  inputClassName?: string;
  error?: string | boolean;
  value: DateRange | undefined;
  label?: string | React.ReactElement;
  onChange?: (newDate: DateRange | undefined, name: string) => void;
  maxRange?: number; // Maximum range in days
}

const dateFormatter = Intl.DateTimeFormat('en', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function DatePickerRange({
  className,
  value,
  onChange,
  onBlur,
  name,
  maxRange,
  ...rest
}: DatePickerRangeProps) {
  const defaultClassNames = getDefaultClassNames();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const datePickerRef = useRef<HTMLDivElement | null>(null); // Reference for DayPicker container
  const datePickerInputRef = useRef<HTMLLabelElement | null>(null); // Reference for DayPicker input

  const [selectedMonth, setSelectedMonth] = useState(value?.from ?? new Date());

  const handleDayPickerSelect = (newDate: DateRange | undefined) => {
    if (onChange) {
      if (newDate?.from) {
        newDate.from = new Date(format(newDate.from, 'yyyy-MM-dd'));
      }
      if (newDate?.to) {
        newDate.to = new Date(format(newDate.to, 'yyyy-MM-dd'));
      }
      onChange(newDate, name ?? '');
    }
  };

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        datePickerInputRef.current &&
        !datePickerRef.current.contains(event.target as Node) &&
        !datePickerInputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false); // Close the date picker if click is outside
        if (onBlur) {
          onBlur(new Event('blur') as unknown as React.FocusEvent<HTMLInputElement>);
        }
        // setSelectedDate(new Date(rest.value));
      }
    },
    [onBlur]
  );

  // Handle clicking outside of the date picker to close it
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mouseup', handleClickOutside);
    } else {
      document.removeEventListener('mouseup', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [handleClickOutside, isOpen]);

  const formattedValue = useMemo(() => {
    if (!value || !value.from) {
      return '';
    }

    const v: string = value?.from
      ? dateFormatter.formatRange(value.from, value?.to ?? value.from)
      : '';
    return v;
  }, [value]);

  return (
    <div className={classNames('relative', className)}>
      <Input
        labelIcon={
          <span className="text-heading inline-block">
            <CalendarDaysIcon size={16} />
          </span>
        }
        onFocus={() => {
          setIsOpen(true);
        }}
        autoComplete="off"
        labelRef={datePickerInputRef}
        value={formattedValue}
        onChange={() => {
          if (onChange) {
            handleDayPickerSelect(undefined);
          }
        }}
        readOnly
        clearable
        focusOnClear={false}
        {...rest}
      />
      {isOpen && (
        <div
          ref={datePickerRef}
          className="absolute z-10 rounded-md border-2 border-neutral-500 bg-white px-3"
        >
          <DayPicker
            timeZone="UTC"
            captionLayout="dropdown"
            mode="range"
            selected={{
              from: value?.from ? new Date(value.from) : undefined,
              to: value?.to ? new Date(value.to) : undefined,
            }}
            onMonthChange={(newMonth) => {
              setSelectedMonth(newMonth);
            }}
            disabled={{ after: new Date() }}
            fixedWeeks
            weekStartsOn={1}
            onSelect={handleDayPickerSelect}
            showOutsideDays
            required={false}
            month={selectedMonth}
            max={maxRange}
            classNames={{
              // day: 'rounded-md',
              range_start: 'range-start bg-blue-600 text-white rounded-l-md', // Highlight the start of the range
              range_end: 'range-end bg-blue-600 text-white rounded-r-md', // Highlight the end of the range
              range_middle: `bg-blue-100 rounded-none text-neutral-800`, // Highlight the range of selected days
              today: `rounded-md bg-blue-50 underline`, // Add a border to today's date
              selected: ``, // Highlight the selected day
              // root: `${defaultClassNames.root} shadow-lg p-5`, // Add a shadow to the root element
              chevron: `fill-blue-600`, // Change the color of the chevron
              // month: 'text-heading',
              button_previous: 'text-green-800 fill-red-800',
              caption_label: `${defaultClassNames.caption_label} text-neutral-800`,
            }}
          />
        </div>
      )}
    </div>
  );
}

export default DatePickerRange;
